import React, { useState } from 'react';
import Stepper from 'components/base/stepper';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { ROUTE_KEYS, STORAGE_KEYS } from 'utils/constants';
import { apiService } from 'services/base.service';
import { ToastMessage } from '@cokitchen/cokitchen-components';
import { toast } from 'react-toastify';
import useLetsGetStartedContext from 'contexts/lets-get-started.context';
import { handleApiError } from 'utils/functions';
import { PersonalInfoFormDataType } from 'models/get-started.model';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';

const LoginComponent = () => (
  <p className='text-[#6F7376] my-4 text-center'>
    Already have an account?
    <Link
      className='cursor-pointer ml-1 text-[#2E3031] font-medium '
      to={`/${ROUTE_KEYS.AUTH}/${ROUTE_KEYS.LOGIN}`}>
      Login
    </Link>
  </p>
);

const LetsGetStartedContent = () => {
  const {
    personalInfoFormData,
    companyInfoFormData,
    passwordFormData,
    disabled,
    setPasswordFormData,
    setPersonalInfoFormData,
    setCompanyInfoFormData
  } = useLetsGetStartedContext();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const letsGetStartedUrl = [
    { name: 'Personal Information', url: `/${ROUTE_KEYS.AUTH}/${ROUTE_KEYS.REGISTER}` },
    {
      name: 'Company details',
      url: `/${ROUTE_KEYS.AUTH}/${ROUTE_KEYS.REGISTER}/${ROUTE_KEYS.COMPANY_DETAILS}`
    },
    {
      name: 'Create Password',
      url: `/${ROUTE_KEYS.AUTH}/${ROUTE_KEYS.REGISTER}/${ROUTE_KEYS.CREATE_PASSWORD}`
    },

    {
      name: 'Verify account',
      url: `/${ROUTE_KEYS.AUTH}/${ROUTE_KEYS.REGISTER}/${ROUTE_KEYS.VERIFY_ACCOUNT}`
    }
  ];

  const registerAccount = async () => {
    const { first_name, last_name, email, phone_number } = personalInfoFormData;
    const data: PersonalInfoFormDataType = {
      first_name,
      last_name,
      email
    };
    if (phone_number) data.phone_number = phone_number;
    setLoading(true);
    try {
      const res = await apiService.post('auth/register', { data });
      localStorage.setItem(STORAGE_KEYS.NEW_USER_ID, res.data.data.id);
      localStorage.setItem(STORAGE_KEYS.NEW_USER_EMAIL, res.data.data.email);
      localStorage.setItem(STORAGE_KEYS.NEW_USER_PHONE_NUMBER, res.data.data.phone_number);

      if (res) {
        setPersonalInfoFormData({
          ...personalInfoFormData,
          first_name: '',
          last_name: '',
          email: '',
          phone_number: ''
        });
      }
    } catch (err) {
      handleApiError(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const userId = localStorage.getItem(STORAGE_KEYS.NEW_USER_ID);
  const registerCompany = async () => {
    setLoading(true);
    const { company_name, company_email, company_address, cac_number, company_logo } =
      companyInfoFormData;
    const geocodes = await geocodeByAddress(company_address);
    const { lat: latitude, lng: longitude } = await getLatLng(geocodes[0]);
    try {
      const data = {
        user_id: userId,
        name: company_name,
        email: company_email,
        address: company_address,
        cac: cac_number,
        logo: company_logo,
        latitude,
        longitude
      };
      await apiService.post('auth/register-company', { data });
      setCompanyInfoFormData({
        ...companyInfoFormData,
        company_name: '',
        company_email: '',
        company_address: '',
        cac_number: '',
        company_logo: ''
      });
    } catch (err) {
      handleApiError(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const createPassword = async () => {
    const { password, confirm_password } = passwordFormData;
    if (password !== confirm_password) {
      toast(<ToastMessage type='error' message='Password does not match' />);
      return false;
    }
    setLoading(true);
    try {
      const data = {
        user_id: userId,
        password
      };
      await apiService.post('auth/password', { data });
      setPasswordFormData({ ...passwordFormData, password: '', confirm_password: '' });
    } catch (err) {
      handleApiError(err);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleNextClick = async (val: number) => {
    switch (val) {
      case 0:
        return await registerAccount();
      case 1:
        return await registerCompany();
      case 2:
        return await createPassword();
      default:
        return;
    }
  };

  const handleConfirm = async () => {
    localStorage.clear();
    navigate('/verification-pending');
  };

  return (
    <Stepper
      onConfirm={handleConfirm}
      pages={letsGetStartedUrl}
      hide
      loading={loading}
      onClick={handleNextClick}
      isDisabled={disabled}
      disableSideNav
      customComponent={<LoginComponent />}>
      <Outlet />
    </Stepper>
  );
};

export default LetsGetStartedContent;
